import React from 'react';
import { graphql } from 'gatsby';

import { useSeoMetada } from '../hooks/useSeoMetadata';

import Layout from '../components/Layout';
import Page from '../components/Page';

const PageContainer = ({ data }) => {
  const {
    seoMetadata: contentfulSeoMetadata,
    header,
    sections,
    withBreadcrumb
  } = data.content;
  const { edges: articles } = data.articles;
  const { helmetMetadata } = useSeoMetada(contentfulSeoMetadata, {
    title: header.title,
    description: header.description?.childMarkdownRemark?.rawMarkdownBody,
    ogImage: header.illustration || header.heroImage
  });

  return (
    <Layout data={data} helmetMetadata={helmetMetadata}>
      <Page
        header={header}
        sections={sections}
        articles={articles}
        withBreadcrumb={withBreadcrumb}
      />
    </Layout>
  );
};

export default PageContainer;

export const PageFragment = graphql`
  fragment PageFragment on ContentfulPage {
    id
    node_locale
    contentful_id
    name
    slug
    withBreadcrumb
    isPageHiddenFromBots
    dataLayer {
      category
      subCategory
      title
    }
    seoMetadata {
      ...SeoMetadataFragment
    }
    header {
      ...HeaderFragment
    }
    sections {
      ...SectionFragment
    }
  }

  fragment ReferenceFragment on ContentfulReference {
    ... on ContentfulTermsCondition {
      id
      contentful_id
      title
      markdownContent {
        ...TermsDescriptionFragment
      }
    }
    ... on ContentfulBlogCategory {
      id
      contentful_id
      title
    }
    ... on ContentfulSection {
      id
      identifier
      version
      contentful_id
      blockContent {
        ...BlockContentFragment
      }
      illustration {
        ...IllustrationFragment
      }
      assets {
        ...AssetFragment
      }
      isFullWidth
      isDarkMode
      hasAdditionalSections
      isAdditionalSection
      backgroundColor
      references {
        ... on ContentfulSection {
          id
          identifier
          version
          contentful_id
          blockContent {
            ...BlockContentFragment
          }
          illustration {
            ...IllustrationFragment
          }
          assets {
            ...AssetFragment
          }
          isFullWidth
          isDarkMode
          hasAdditionalSections
          isAdditionalSection
          backgroundColor
          references {
            ... on ContentfulSection {
              id
              identifier
              version
              contentful_id
              blockContent {
                ...BlockContentFragment
              }
              illustration {
                ...IllustrationFragment
              }
              assets {
                ...AssetFragment
              }
              isFullWidth
              isDarkMode
              hasAdditionalSections
              isAdditionalSection
              backgroundColor
            }
          }
        }
      }
    }

    ... on ContentfulQuestion {
      ...QuestionFragment
    }

    ... on ContentfulTestimonial {
      ...TestimonialFragment
    }

    ... on ContentfulNumbers {
      ...NumberFragment
    }
  }

  fragment SectionFragment on ContentfulSection {
    id
    identifier
    version
    contentful_id
    isFullWidth
    hasAdditionalSections
    isAdditionalSection
    isDarkMode
    backgroundColor
    additionalClassNames
    blockContent {
      ...BlockContentFragment
    }
    illustration {
      ...IllustrationFragment
    }
    assets {
      ...AssetFragment
    }
    references {
      ...ReferenceFragment
    }
  }
  fragment TermsFragment on ContentfulTermsCondition {
    id
    contentful_id
    title
    identifier
    markdownContent {
      ...MarkdownFragmentTerms
    }
  }

  fragment HeaderFragment on ContentfulHeader {
    id
    blockContent {
      ...BlockContentFragment
    }
    illustration {
      ...IllustrationFragment
    }
    isNavbarTransparent
  }

  fragment SeoMetadataFragment on ContentfulSeoMetadata {
    id
    title
    description
    keywords
    ogImage {
      ...AssetFragment
    }
  }

  fragment ButtonFragment on ContentfulButton {
    id
    contentful_id
    name
    version
    label
    subLabel
    formattedLabel {
      childMarkdownRemark {
        html
      }
    }
    isNew
    externalLink
    scrollTo {
      ... on ContentfulSection {
        id
        contentful_id
        identifier
      }
      ... on ContentfulTermsCondition {
        ...TermsFragment
      }
    }

    icon
    iconPosition
    isDownload
    page {
      id
      node_locale
      name
      gatsbyPath(
        filePath: "/{ContentfulPage.node_locale}/{ContentfulPage.slug}"
      )
    }
    article {
      id
      node_locale
      gatsbyPath(
        filePath: "/{ContentfulArticle.node_locale}/blog/blogpost/{ContentfulArticle.slug}"
      )
    }
    dataLayer {
      category
      action
      label
    }
    isOpenAxeptioModal
  }

  fragment TestimonialFragment on ContentfulTestimonial {
    id
    contentful_id
    userName
    quote {
      quote
    }
    userTitle
    userCompany
    avatar {
      ...AssetFragment
    }
  }

  fragment AssetFragment on ContentfulAsset {
    id
    title
    description
    gatsbyImageData(placeholder: NONE, width: 2400)
    file {
      url
    }
  }

  fragment NumberFragment on ContentfulNumbers {
    numbers {
      id
      title
      description
    }
  }

  fragment SectionDescriptionFragment on contentfulSectionDescriptionTextNode {
    childMarkdownRemark {
      html
      rawMarkdownBody
    }
  }

  fragment HeaderDescriptionFragment on contentfulHeaderDescriptionTextNode {
    childMarkdownRemark {
      html
      rawMarkdownBody
    }
  }

  fragment TermsDescriptionFragment on contentfulTermsConditionMarkdownContentTextNode {
    childMarkdownRemark {
      html
      rawMarkdownBody
    }
  }

  fragment ArticleFragment on ContentfulArticle {
    id
    contentful_id
    node_locale
    title
    slug
    approvedPublished
    gatsbyPath(
      filePath: "/{ContentfulArticle.node_locale}/blog/blogpost/{ContentfulArticle.slug}"
    )
    markdownContent {
      ...MarkdownFragmentArticle
    }
    subtitle {
      ...ArticleSubtitleFragment
    }
    categories {
      id
      title
    }
    coverImage {
      ...AssetFragment
    }
    author {
      ...AuthorFragment
    }
    reviewState
    approvedPublished
    publishedAt(formatString: "LL", locale: "en-US")
  }

  fragment AuthorFragment on ContentfulAuthor {
    id
    contentful_id
    node_locale
    slug
    firstName
    lastName
    linkLinkedin
    gatsbyPath(
      filePath: "/{ContentfulAuthor.node_locale}/blog/authors/{ContentfulAuthor.slug}"
    )
    bio {
      childMarkdownRemark {
        html
        rawMarkdownBody
      }
    }
    avatar {
      ...AssetFragment
    }
  }

  fragment MarkdownFragmentArticle on contentfulArticleMarkdownContentTextNode {
    childMarkdownRemark {
      rawMarkdownBody
      html
    }
  }

  fragment MarkdownFragmentTerms on contentfulTermsConditionMarkdownContentTextNode {
    childMarkdownRemark {
      rawMarkdownBody
      html
    }
  }

  fragment ArticleSubtitleFragment on contentfulArticleSubtitleTextNode {
    childMarkdownRemark {
      html
      rawMarkdownBody
      timeToRead
    }
  }

  fragment JobListingFragment on lever {
    categories {
      team
      commitment
      location
    }
    description
    text
    hostedUrl
    id
  }

  fragment NavbarFragment on ContentfulNavbar {
    id
    node_locale
    name
    links {
      ...ButtonFragment
      subButtons {
        ...ButtonFragment
      }
    }
    buttons {
      ...ButtonFragment
    }
  }

  fragment FooterFragment on ContentfulFooter {
    id
    node_locale
    name
    aboutUsTitle
    legalTitle
    resourcesTitle
    usefulLinksTitle
    companyInfoTitle
    companyInfo {
      ...ButtonFragment
    }
    aboutUs {
      ...ButtonFragment
    }
    legal {
      ...ButtonFragment
    }
    resources {
      ...ButtonFragment
    }
    usefulLinks {
      ...ButtonFragment
    }
  }

  fragment QuestionFragment on ContentfulQuestion {
    id
    contentful_id
    title
    description {
      childMarkdownRemark {
        html
      }
    }
    button {
      ...ButtonFragment
    }
    link
  }

  fragment PagePathFragment on ContentfulPage {
    id
    contentful_id
    slug
    node_locale
    name
    gatsbyPath(filePath: "/{ContentfulPage.node_locale}/{ContentfulPage.slug}")
  }

  fragment AuthorPathFragment on ContentfulAuthor {
    id
    node_locale
    contentful_id
    gatsbyPath(
      filePath: "/{ContentfulAuthor.node_locale}/blog/authors/{ContentfulAuthor.slug}"
    )
  }
  fragment ArticlePathFragment on ContentfulArticle {
    id
    node_locale
    contentful_id
    gatsbyPath(
      filePath: "/{ContentfulArticle.node_locale}/blog/blogpost/{ContentfulArticle.slug}"
    )
  }

  fragment BlockContentFragment on ContentfulBlockContent {
    id
    node_locale
    contentful_id
    label
    title
    subtitle
    headingOne
    headingTwo
    description {
      childMarkdownRemark {
        html
        rawMarkdownBody
      }
    }
    isDescriptionSmall
    buttons {
      ...ButtonFragment
    }
  }

  fragment IllustrationFragment on ContentfulIllustration {
    id
    file {
      ...AssetFragment
    }
    url
    isBackgroundImage
    position
    isTranslate
  }
`;
